import type { AxiosError, AxiosHeaders, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'

import { marketDataApiBaseURL } from '@/utils/config.ts'

import { AuthTokenStorageKey } from '../state/authToken'

/**
 * Subset of AxiosRequestConfig
 */
export type RequestConfig<TData = unknown> = {
    baseURL?: string
    url?: string
    method: 'get' | 'put' | 'patch' | 'post' | 'delete'
    params?: unknown
    data?: TData
    responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream'
    signal?: AbortSignal
    headers?: AxiosRequestConfig['headers']
}
/**
 * Subset of AxiosResponse
 */
export type ResponseConfig<TData = unknown> = {
    data: TData
    status: number
    statusText: string
    headers?: AxiosResponse['headers']
}

export const axiosInstance = axios.create({
    baseURL: marketDataApiBaseURL,
    withCredentials: true,
    headers: import.meta.env.AXIOS_HEADERS ? (JSON.parse(import.meta.env.AXIOS_HEADERS) as AxiosHeaders) : undefined,
})

export const axiosClient = async <TData, TError = unknown, TVariables = unknown>(
    config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
    config.headers ??= {}
    const authToken = localStorage.getItem(AuthTokenStorageKey)
    if (authToken && authToken !== 'undefined') {
        const trimmedAuthToken = authToken.replace(/^['"]+|['"]+$/g, '')
        config.headers['Authorization'] = `Bearer ${trimmedAuthToken}`
    }

    return axiosInstance.request<TData, ResponseConfig<TData>>(config).catch((e: AxiosError<TError>) => {
        throw e
    })
}

export default axiosClient
